import { useEffect, useMemo, useState } from "react";
import { navigateTo } from "../../router";
import { Board } from "./components/Board";
import { Market } from "./components/Market";
import { NatureTokenTray } from "./components/NatureTokenTray";
import { PlayersPanel } from "./components/PlayersPanel";
import { ScoringCardPanel } from "./components/ScoringCardPanel";
import { CASCADIA_REPLAY_FIXTURES } from "./replay-fixtures.generated";
import {
  buildCascadiaReplaySnapshots,
  type CascadiaReplaySnapshot,
} from "./replay-runner";
import "./styles/cascadia-tokens.css";

interface ReplayScreenProps {
  readonly scenario: string;
}

const PHASE_LABELS: Readonly<Record<string, string>> = {
  marketCheck: "시장/드래프트",
  placeTile: "타일 배치",
  placeWildlife: "야생동물 배치",
  unlockLandmark: "랜드마크 잠금 해제",
  endTurn: "턴 종료",
  scoring: "점수 집계",
};

function actorName(
  snapshot: CascadiaReplaySnapshot,
  namesByPlayerId: Readonly<Record<string, string>>,
): string {
  return namesByPlayerId[snapshot.event.userId] ?? snapshot.event.userId;
}

function eventSummary(snapshot: CascadiaReplaySnapshot): string {
  return `#${snapshot.event.sequenceNumber} ${snapshot.event.eventType}`;
}

function clampStepIndex(
  index: number,
  snapshots: readonly CascadiaReplaySnapshot[],
): number {
  if (snapshots.length === 0) return 0;
  return Math.min(Math.max(0, index), snapshots.length - 1);
}

function parseInteger(value: string | null): number | null {
  if (value === null || value.trim() === "") return null;
  const parsed = Number(value);
  return Number.isInteger(parsed) ? parsed : null;
}

function resolveStepIndexFromSearch(
  snapshots: readonly CascadiaReplaySnapshot[],
): number {
  if (typeof window === "undefined" || snapshots.length === 0) return 0;

  const params = new URLSearchParams(window.location.search);
  const sequenceNumber = parseInteger(params.get("seq"));
  if (sequenceNumber !== null) {
    const index = snapshots.findIndex(
      (snapshot) => snapshot.event.sequenceNumber === sequenceNumber,
    );
    if (index >= 0) return index;
  }

  const step = parseInteger(params.get("step"));
  if (step !== null) return clampStepIndex(step, snapshots);

  return 0;
}

function replaceStepQuery(
  index: number,
  snapshots: readonly CascadiaReplaySnapshot[],
): void {
  if (typeof window === "undefined" || snapshots.length === 0) return;

  const url = new URL(window.location.href);
  url.searchParams.set("step", String(index));
  url.searchParams.delete("seq");
  window.history.replaceState(null, "", `${url.pathname}${url.search}`);
}

export function ReplayScreen({ scenario }: ReplayScreenProps) {
  const fixture = CASCADIA_REPLAY_FIXTURES[scenario] ?? null;
  const snapshots = useMemo(
    () => (fixture ? buildCascadiaReplaySnapshots(fixture.events) : []),
    [fixture],
  );
  const [stepIndex, setStepIndex] = useState(() =>
    resolveStepIndexFromSearch(snapshots),
  );
  const namesByPlayerId = useMemo(
    () =>
      fixture
        ? Object.fromEntries(
            fixture.players.map((player) => [player.id, player.name]),
          )
        : {},
    [fixture],
  );
  const safeStepIndex = clampStepIndex(stepIndex, snapshots);
  const current = snapshots[safeStepIndex] ?? snapshots[0] ?? null;

  useEffect(() => {
    setStepIndex(resolveStepIndexFromSearch(snapshots));
  }, [snapshots]);

  function setReplayStep(index: number): void {
    const nextIndex = clampStepIndex(index, snapshots);
    setStepIndex(nextIndex);
    replaceStepQuery(nextIndex, snapshots);
  }

  if (!fixture || !current) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-primary p-6 text-text-primary">
        <section className="max-w-sm rounded-md border border-border-primary bg-surface-secondary p-6 text-center">
          <h1 className="mb-2 font-semibold text-lg">
            리플레이를 찾을 수 없습니다
          </h1>
          <p className="mb-4 text-sm text-text-secondary">{scenario}</p>
          <button
            type="button"
            className="rounded-md bg-accent-blue px-4 py-2 font-semibold text-sm text-white"
            onClick={() => navigateTo("/")}
          >
            닫기
          </button>
        </section>
      </div>
    );
  }

  const state = current.state;
  const { turn, players, market, scoringCards } = state;
  const activeSeat = turn.activeSeat;
  const activePlayer = players[activeSeat];
  const activeBoard = activePlayer?.board ?? { cells: {} };
  const maxIndex = snapshots.length - 1;
  const phaseLabel = PHASE_LABELS[turn.phase] ?? turn.phase;
  const isGameOver = turn.phase === "scoring";

  const noop = () => undefined;

  return (
    <div
      className="min-h-screen"
      style={{
        background: "var(--cn-cascadia-bg)",
        color: "var(--cn-cascadia-label)",
      }}
      data-testid="cascadia-replay-screen"
    >
      <div
        className="sticky top-0 z-20 border-border-primary border-b bg-surface-primary/95 px-4 py-3 backdrop-blur"
        data-testid="cascadia-replay-toolbar"
      >
        <div className="mx-auto flex max-w-5xl flex-col gap-3 md:flex-row md:items-center">
          <div className="min-w-0 flex-1">
            <p
              className="truncate font-semibold text-sm text-text-primary"
              data-testid="cascadia-replay-event-summary"
            >
              {eventSummary(current)} · {actorName(current, namesByPlayerId)}
            </p>
            <p
              className="text-text-secondary text-xs"
              data-testid="cascadia-replay-phase"
            >
              {phaseLabel} · 현재 좌석 {activeSeat}
              {activePlayer ? ` (${activePlayer.displayName})` : ""}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="rounded-md border border-border-primary px-3 py-2 font-semibold text-sm text-text-primary disabled:opacity-40"
              data-testid="cascadia-replay-prev"
              disabled={safeStepIndex === 0}
              onClick={() => setReplayStep(safeStepIndex - 1)}
            >
              Prev
            </button>
            <input
              type="range"
              min={0}
              max={maxIndex}
              value={safeStepIndex}
              className="w-36 md:w-56"
              data-testid="cascadia-replay-step-slider"
              onChange={(event) => setReplayStep(Number(event.target.value))}
            />
            <button
              type="button"
              className="rounded-md border border-border-primary px-3 py-2 font-semibold text-sm text-text-primary disabled:opacity-40"
              data-testid="cascadia-replay-next"
              disabled={safeStepIndex === maxIndex}
              onClick={() => setReplayStep(safeStepIndex + 1)}
            >
              Next
            </button>
            <span
              className="w-16 text-right font-semibold text-sm text-text-secondary"
              data-testid="cascadia-replay-step-count"
            >
              {safeStepIndex + 1}/{snapshots.length}
            </span>
          </div>
        </div>
      </div>

      <div className="mx-auto flex max-w-5xl flex-col gap-4 p-4 md:flex-row">
        <aside className="flex flex-col gap-3 md:w-60">
          <PlayersPanel
            players={players}
            activeSeat={activeSeat}
            myPlayerIndex={null}
          />
          {activePlayer ? (
            <NatureTokenTray
              count={activePlayer.natureTokens}
              spentThisTurn={0}
              estimatedScore={activePlayer.scoreBreakdown?.total ?? null}
            />
          ) : null}
          {isGameOver ? (
            <div
              className="rounded-md border border-border-primary bg-surface-secondary p-3 text-center font-semibold text-sm text-text-primary"
              data-testid="cascadia-replay-game-over"
            >
              게임 종료
              {state.winners && state.winners.length > 0
                ? ` · 승자 ${state.winners.join(", ")}`
                : " · 집계 대기"}
            </div>
          ) : null}
        </aside>

        <div className="min-w-0 flex-1">
          <Board
            board={activeBoard}
            phase={turn.phase}
            draftedWildlife={null}
            onTapCoord={noop}
            disabled={true}
          />
        </div>

        <aside className="flex flex-col gap-3 md:w-64">
          <Market
            market={market}
            phase={turn.phase}
            activePlayerNatureTokens={activePlayer?.natureTokens ?? 0}
            selectedSlot={null}
            onDraftPair={noop}
            onWipeOptional={noop}
            onSpendNatureDecouple={noop}
            layout="rail"
          />
          <ScoringCardPanel scoringCards={scoringCards} />
        </aside>
      </div>
    </div>
  );
}
